// Book a table — the same single column as the checkout. The store's
// booking book is the server's (GET …/booking-settings): the page offers
// only the guests, dates and times it would accept — the pax limit, the
// weekly sessions, the closed dates, the lead time against the store's own
// clock, the horizon. The server re-checks all of it inside the placing
// transaction; a refusal (the book changed while the customer looked)
// renders as an alert and the book is re-read. Capacity per session is
// only known to the server: a full session is a refusal too.
//
// The booking rides the device's customer identity, and the profile behind
// it is claimed first (PUT …/customer/me) — that is what links the booking
// to "My bookings".

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { VerifyPhone } from '../../components/VerifyPhone';
import { verifiedPhone } from '../../lib/auth';
import {
  ApiError, bookableDates, bookableTimes, dateLabel, fetchBookingConfig, fetchStore, placeBooking, putMe, storeClock, timeLabel, toE164,
  type BookingConfig, type StorePublic,
} from '../../lib/api';

const DETAILS_KEY = 'rp-order-details'; // shared with the checkout: one name + number per device

/** The refusals that mean the book moved under the customer — re-read it. */
const STALE = new Set(['bookings_disabled', 'pax_over_limit', 'outside_sessions', 'date_unavailable', 'too_soon', 'too_far_ahead', 'session_full']);

export function Book() {
  const { rid, sid } = useParams() as { rid: string; sid: string };
  const nav = useNavigate();
  const [store, setStore] = useState<StorePublic | null>(null);
  const [cfg, setCfg] = useState<BookingConfig | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pax, setPax] = useState(2);
  const [date, setDate] = useState<string | null>(null);
  const [time, setTime] = useState<string | null>(null);
  const saved = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(DETAILS_KEY) ?? '{}') as { name?: string; phone?: string };
    } catch {
      return {};
    }
  }, []);
  const [name, setName] = useState(saved.name ?? '');
  const [phone, setPhone] = useState(saved.phone ?? '');
  const [notes, setNotes] = useState('');
  // a verified mobile keeps the booking in My bookings (the profile needs it)
  const [verifiedAs, setVerifiedAs] = useState<string | null>(() => verifiedPhone());
  const [verifyDown, setVerifyDown] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<{ code: string; message: string } | null>(null);

  const load = () =>
    Promise.all([fetchStore(rid, sid), fetchBookingConfig(rid, sid)]).then(
      ([s, c]) => {
        setStore(s);
        setCfg(c);
        document.title = `${s.name} — Book a table`;
      },
      (e) => setLoadError(e instanceof Error ? e.message : 'Could not load the booking book.'),
    );

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rid, sid]);

  // the store's clock, read again with every read of the book
  const clock = useMemo(() => (store && cfg ? storeClock(store.timezone) : null), [store, cfg]);
  const dates = useMemo(() => (cfg && clock ? bookableDates(cfg, clock) : []), [cfg, clock]);
  const times = useMemo(() => (cfg && clock && date ? bookableTimes(cfg, date, clock) : []), [cfg, clock, date]);
  const maxPax = cfg?.settings.maxPaxPerBooking ?? 0;

  // a choice the (re-read) book no longer offers is dropped, never silently kept
  useEffect(() => {
    if (date && !dates.includes(date)) setDate(null);
  }, [dates, date]);
  useEffect(() => {
    if (time && !times.includes(time)) setTime(null);
  }, [times, time]);
  useEffect(() => {
    if (maxPax > 0 && pax > maxPax) setPax(maxPax);
  }, [maxPax, pax]);

  if (loadError) return <div className="errbox" role="alert">{loadError}</div>;
  if (!store || !cfg) return <div className="loading">Opening the book…</div>;

  const back = `/r/${rid}/s/${sid}`;
  const header = (
    <header className="co-head">
      <Link to={back} className="co-backbtn" aria-label="Back to the menu">‹</Link>
      <div>
        <div className="co-store">{store.name}</div>
        <h1>Book a table</h1>
      </div>
      <Link to={`/r/${rid}/s/${sid}/bookings`} className="linky bk-mine">My bookings</Link>
    </header>
  );

  if (!cfg.settings.enabled) {
    return (
      <div className="co-page">
        {header}
        <section className="co-sec" data-section="book-closed">
          <p className="bk-lede">{store.name} isn't taking bookings online right now.</p>
          <p className="bk-lede">Call <a className="linky" href={`tel:${store.phoneE164}`}>{store.phoneE164}</a> to book.</p>
        </section>
      </div>
    );
  }

  async function book() {
    if (busy || !date || !time) return;
    const e164 = toE164(phone);
    if (!name.trim() || !e164) {
      setRefusal({ code: 'details', message: 'Add your name and a mobile number the store can reach.' });
      return;
    }
    setBusy(true);
    setRefusal(null);
    try {
      localStorage.setItem(DETAILS_KEY, JSON.stringify({ name: name.trim(), phone: phone.trim() }));
      // the profile behind this device's identity: best effort, so "My bookings" knows me
      // (only a verified number may hold one)
      if (verifiedAs === e164) {
        try {
          await putMe(rid, { name: name.trim(), phone: e164 });
        } catch {
          /* the booking still carries name + phone */
        }
      }
      const b = await placeBooking(rid, sid, { bookingDate: date, bookingTime: time, pax, customerName: name.trim(), customerPhone: e164, notes: notes.trim() || null });
      nav(`/r/${rid}/s/${sid}/bookings/${b.bookingId}`, { replace: true, state: { booking: b, placed: true } });
    } catch (e) {
      if (e instanceof ApiError) {
        setRefusal({ code: e.code, message: e.message });
        if (STALE.has(e.code)) load();
      } else {
        setRefusal({ code: 'unknown', message: 'Could not place the booking.' });
      }
    } finally {
      setBusy(false);
    }
  }

  const phoneReady = (!!verifiedAs && verifiedAs === toE164(phone)) || verifyDown;
  const ready = !!date && !!time;

  return (
    <div className="co-page">
      {header}

      <section className="co-sec" data-section="party">
        <h2>Guests</h2>
        <div className="bk-pills">
          {Array.from({ length: maxPax }, (_, i) => i + 1).map((n) => (
            <button key={n} className={'bk-pill' + (pax === n ? ' on' : '')} aria-pressed={pax === n} aria-label={`${n} ${n === 1 ? 'guest' : 'guests'}`} onClick={() => setPax(n)}>{n}</button>
          ))}
        </div>
        <div className="co-hint">Online bookings take up to {maxPax} guests — call the store for more.</div>
      </section>

      <section className="co-sec" data-section="date">
        <h2>Date</h2>
        {dates.length === 0 ? (
          <div className="co-hint">No dates are open for booking right now.</div>
        ) : (
          <div className="bk-days">
            {dates.map((d) => (
              <button key={d} className={'bk-day' + (date === d ? ' on' : '')} aria-pressed={date === d} aria-label={`Date: ${dateLabel(d)}`} data-date={d} onClick={() => setDate(d)}>
                <span className="dw">{d === clock!.today ? 'Today' : dateLabel(d, { weekday: 'short' })}</span>
                <span className="dn">{dateLabel(d, { day: 'numeric' })}</span>
                <span className="dm">{dateLabel(d, { month: 'short' })}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      {date && (
        <section className="co-sec" data-section="time">
          <h2>Time</h2>
          <div className="bk-pills">
            {times.map((t) => (
              <button key={t} className={'bk-pill bk-time' + (time === t ? ' on' : '')} aria-pressed={time === t} aria-label={`Time: ${timeLabel(t)}`} data-time={t} onClick={() => setTime(t)}>{timeLabel(t)}</button>
            ))}
          </div>
        </section>
      )}

      <section className="co-sec" data-section="details">
        <h2>Your details</h2>
        <input className="co-input" placeholder="Name" aria-label="Name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
        <input className="co-input" placeholder="Mobile number" aria-label="Mobile number" autoComplete="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <VerifyPhone phone={toE164(phone)} verified={!!verifiedAs && verifiedAs === toE164(phone)} onVerified={(v) => setVerifiedAs(v.user.phoneE164)} onUnavailable={() => setVerifyDown(true)} />
        <input className="co-input" placeholder="Anything we should know? (optional)" aria-label="A note for the store" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} />
      </section>

      {refusal && <div className="co-err bk-err" role="alert" data-refusal={refusal.code}>{refusal.message}</div>}

      <div className="co-placebar">
        <button className="co-place" onClick={book} disabled={busy || !ready || !phoneReady}>
          {busy ? 'Booking…' : ready && !phoneReady ? 'Verify your number to book' : ready ? `Book for ${pax} · ${dateLabel(date!)} · ${timeLabel(time!)}` : 'Pick a date and time'}
        </button>
        <div className="co-fine">The store confirms every booking — you'll see it in My bookings.</div>
      </div>
    </div>
  );
}
